import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useCampaignId } from '../hooks/useCampaignId';

type Method = { id: string; key: string; label: string; sort_order: number; is_active: boolean }
type Outcome = { id: string; key: string; label: string; sort_order: number; is_active: boolean }

export default function SettingsContacts() {
  const campaignId = useCampaignId();

  const [methods, setMethods] = useState<Method[]>([])
  const [outcomes, setOutcomes] = useState<Outcome[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  // new rows draft
  const [newMethod, setNewMethod] = useState({ key: '', label: '' })
  const [newOutcome, setNewOutcome] = useState({ key: '', label: '' })

  useEffect(() => {
    if (!campaignId) return;
    (async () => { await refresh(campaignId); setLoading(false); })();
  }, [campaignId])

  const refresh = async (cid: string) => {
    setErr(null);
    setLoading(true);
    const [mRes, oRes] = await Promise.all([
      supabase
        .from('campaign_contact_methods')
        .select('id, key, label, sort_order, is_active')
        .eq('campaign_id', cid)
        .order('sort_order', { ascending: true }),
      supabase
        .from('campaign_contact_outcomes')
        .select('id, key, label, sort_order, is_active')
        .eq('campaign_id', cid)
        .order('sort_order', { ascending: true }),
    ])
    if (mRes.error) setErr(mRes.error.message); else setMethods((mRes.data ?? []) as Method[])
    if (oRes.error) setErr(oRes.error.message); else setOutcomes((oRes.data ?? []) as Outcome[])
    setLoading(false);
  }

  const addMethod = async () => {
    if (!campaignId || !newMethod.key.trim() || !newMethod.label.trim()) return
    const maxOrder = methods.reduce((m, r) => Math.max(m, r.sort_order ?? 0), 0)
    const { error } = await supabase.from('campaign_contact_methods').insert([{
      campaign_id: campaignId,
      key: newMethod.key.trim(),
      label: newMethod.label.trim(),
      sort_order: maxOrder + 1,
      is_active: true
    }])
    if (error) { alert(error.message); return }
    setNewMethod({ key: '', label: '' })
    refresh(campaignId)
  }

  const addOutcome = async () => {
    if (!campaignId || !newOutcome.key.trim() || !newOutcome.label.trim()) return
    const maxOrder = outcomes.reduce((m, r) => Math.max(m, r.sort_order ?? 0), 0)
    const { error } = await supabase.from('campaign_contact_outcomes').insert([{
      campaign_id: campaignId,
      key: newOutcome.key.trim(),
      label: newOutcome.label.trim(),
      sort_order: maxOrder + 1,
      is_active: true
    }])
    if (error) { alert(error.message); return }
    setNewOutcome({ key: '', label: '' })
    refresh(campaignId)
  }

  const toggleActive = async (
    table: 'campaign_contact_methods' | 'campaign_contact_outcomes',
    id: string,
    current: boolean
  ) => {
    if (!campaignId) return
    const { error } = await supabase.from(table).update({ is_active: !current }).eq('id', id)
    if (error) { alert(error.message); return }
    refresh(campaignId)
  }

  const renameRow = async (
    table: 'campaign_contact_methods' | 'campaign_contact_outcomes',
    id: string,
    label: string
  ) => {
    if (!campaignId) return
    const { error } = await supabase.from(table).update({ label }).eq('id', id)
    if (error) { alert(error.message); return }
    refresh(campaignId)
  }

  const moveRow = async (
    table: 'campaign_contact_methods' | 'campaign_contact_outcomes',
    rows: (Method | Outcome)[],
    id: string,
    dir: -1 | 1
  ) => {
    if (!campaignId) return
    const idx = rows.findIndex(r => r.id === id)
    if (idx < 0) return
    const j = idx + dir
    if (j < 0 || j >= rows.length) return
    const a = rows[idx], b = rows[j]
    const { error } = await supabase.from(table).upsert([
      { id: a.id, sort_order: b.sort_order },
      { id: b.id, sort_order: a.sort_order }
    ])
    if (error) { alert(error.message); return }
    refresh(campaignId)
  }

  if (!campaignId) return <div style={{ padding: 24 }}>Select a campaign from the top menu to edit contact settings.</div>
  if (loading) return <div style={{ padding: 24 }}>Loading…</div>
  if (err) return <div style={{ padding: 24, color: 'crimson' }}>Error: {err}</div>

  return (
    <div style={{ padding: 24 }}>
      <h1>Campaign Settings → Contacts</h1>

      {/* Methods */}
      <section style={{ marginTop: 24 }}>
        <h2>Contact Methods</h2>
        <div style={{ display:'flex', gap:8, margin:'8px 0 12px' }}>
          <input
            placeholder="key (e.g., phone)"
            value={newMethod.key}
            onChange={e=>setNewMethod(s=>({...s, key:e.target.value}))}
          />
          <input
            placeholder="label (e.g., Phone)"
            value={newMethod.label}
            onChange={e=>setNewMethod(s=>({...s, label:e.target.value}))}
            style={{ flex:1 }}
          />
          <button onClick={addMethod} disabled={!newMethod.key.trim() || !newMethod.label.trim()}>Add</button>
        </div>

        <table cellPadding={6} style={{ borderCollapse:'collapse', width:'100%', background:'#fff' }}>
          <thead>
            <tr>
              <th align="left" style={{width:120}}>Key</th>
              <th align="left">Label</th>
              <th align="left" style={{width:120}}>Active</th>
              <th align="left" style={{width:200}}>Reorder</th>
              <th align="left" style={{width:200}}>Actions</th>
            </tr>
          </thead>
          <tbody>
          {methods.map((m, i) => (
            <tr key={m.id} style={{ borderTop:'1px solid #eee' }}>
              <td>{m.key}</td>
              <td>
                <InlineEdit value={m.label} onSave={(val)=>renameRow('campaign_contact_methods', m.id, val)} />
              </td>
              <td>{m.is_active ? 'Yes' : 'No'}</td>
              <td>
                <button onClick={()=>moveRow('campaign_contact_methods', methods, m.id, -1)} disabled={i===0}>↑</button>
                <button onClick={()=>moveRow('campaign_contact_methods', methods, m.id, +1)} disabled={i===methods.length-1} style={{marginLeft:8}}>↓</button>
              </td>
              <td>
                <button onClick={()=>toggleActive('campaign_contact_methods', m.id, m.is_active)}>
                  {m.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </section>

      {/* Outcomes */}
      <section style={{ marginTop: 32 }}>
        <h2>Contact Outcomes</h2>
        <div style={{ display:'flex', gap:8, margin:'8px 0 12px' }}>
          <input
            placeholder="key (e.g., contacted)"
            value={newOutcome.key}
            onChange={e=>setNewOutcome(s=>({...s, key:e.target.value}))}
          />
          <input
            placeholder="label (e.g., Contacted)"
            value={newOutcome.label}
            onChange={e=>setNewOutcome(s=>({...s, label:e.target.value}))}
            style={{ flex:1 }}
          />
          <button onClick={addOutcome} disabled={!newOutcome.key.trim() || !newOutcome.label.trim()}>Add</button>
        </div>

        <table cellPadding={6} style={{ borderCollapse:'collapse', width:'100%', background:'#fff' }}>
          <thead>
            <tr>
              <th align="left" style={{width:140}}>Key</th>
              <th align="left">Label</th>
              <th align="left" style={{width:120}}>Active</th>
              <th align="left" style={{width:200}}>Reorder</th>
              <th align="left" style={{width:200}}>Actions</th>
            </tr>
          </thead>
          <tbody>
          {outcomes.map((o, i) => (
            <tr key={o.id} style={{ borderTop:'1px solid #eee' }}>
              <td>{o.key}</td>
              <td>
                <InlineEdit value={o.label} onSave={(val)=>renameRow('campaign_contact_outcomes', o.id, val)} />
              </td>
              <td>{o.is_active ? 'Yes' : 'No'}</td>
              <td>
                <button onClick={()=>moveRow('campaign_contact_outcomes', outcomes, o.id, -1)} disabled={i===0}>↑</button>
                <button onClick={()=>moveRow('campaign_contact_outcomes', outcomes, o.id, +1)} disabled={i===outcomes.length-1} style={{marginLeft:8}}>↓</button>
              </td>
              <td>
                <button onClick={()=>toggleActive('campaign_contact_outcomes', o.id, o.is_active)}>
                  {o.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}

function InlineEdit({ value, onSave }: { value: string; onSave: (v:string)=>void }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value)
  useEffect(()=>setVal(value), [value])

  if (!editing) return (
    <span style={{ cursor:'pointer' }} onClick={()=>setEditing(true)} title="Click to edit">
      {value}
    </span>
  )
  return (
    <span>
      <input value={val} onChange={e=>setVal(e.target.value)} style={{ minWidth:240 }} />
      <button onClick={()=>{ onSave(val.trim()); setEditing(false) }} style={{ marginLeft:8 }}>Save</button>
      <button onClick={()=>{ setVal(value); setEditing(false) }} style={{ marginLeft:4 }}>Cancel</button>
    </span>
  )
}

